import FilterAlt from '@mui/icons-material/FilterAlt';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MarkChatReadOutlinedIcon from '@mui/icons-material/MarkChatReadOutlined';
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CreateTicketDialog from 'components/common/CreateTicketDialog';
import AlertDialog from 'components/UI/AlertDialog';
import { PrimaryBtn } from 'components/UI/Buttons';
import InfoDialog from 'components/UI/InfoDialog';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import OptionsDialog from 'components/UI/OptionsDialog';
import useCurrentUser from 'hooks/useCurrentUser';
import React, { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetBusinessUnitBranchOfficesQuery, useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import {
  useCloseLeadMutation,
  useExtractClientInfoMutation,
  useGetCreditStatusMutation,
  useMarkAsReadMutation,
  useMarkAsUnreadMutation,
  useUpdateEditableAttributesMutation,
  useUploadClientToCRMMutation,
} from 'services/api/clients.api';
import type { Client } from 'services/types/client';
import { ALLOWED_CLOSE_LEAD_STATUSES } from 'services/types/lead';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_TINT,
  YELLOW,
} from 'styles/tokens';
import {
  AUTOLAND_COLOMBIA_VENTAS_BUSINESS_UNIT_ID,
  FINANCIAL_INSTITUTION_BUSINESS_UNIT_IDS,
  SALFA_NURTURING_ID,
} from 'utils/constants';
import canChangeAutomaticResponses from 'utils/validations/canChangeAutomaticResponses';

import WhatsAppWindowBadge from './components/WhatsAppWindowBadge';
import TemplateMessageModal from './TemplateMessageModal';

// Sentinel for "let the backend resolve the branch". An empty string would leave the select rendering blank.
const AUTOMATIC_BRANCH_OFFICE = 'auto';

interface Props {
  client: Client;
  refetch: () => void;
  setIsFilteredConversation: (isFiltered: boolean) => void;
  isFilteredConversation: boolean;
  isLoading: boolean;
  isLastLeadClosed: boolean;
  windowExpiresAt: Date | null;
}

const ChatHeader: FC<Props> = ({
  client,
  refetch,
  setIsFilteredConversation,
  isFilteredConversation,
  isLoading,
  isLastLeadClosed,
  windowExpiresAt,
}) => {
  const uploadableBusinessIds = [
    2, 3, 8, 10, 11, 14, 16, 17, 18, 19, 20, 21, 22, 24, 27, 36, 39, 41, 44, 45, 49, 50, 51, 52, 53, 54, 65, 69, 70, 72,
    80, 83, 87, 94, 96, 109, 131, 132, 146, 149, 164, 172, 178,
  ];

  const predefinedNotesBusinessUnitIds = [39];

  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openScheduleTemplateModal, setOpenScheduleTemplateModal] = useState<boolean>(false);
  const [openUploadToCRMModal, setOpenUploadToCRMModal] = useState<boolean>(false);
  const [uploadToGenesys, setUploadToGenesys] = useState<boolean>(false);
  const [genesysCentralizerId, setGenesysCentralizerId] = useState<string>('');
  const [genesysCentralizerIdError, setGenesysCentralizerIdError] = useState<boolean>(false);
  const [crmBranchOffice, setCrmBranchOffice] = useState<string>(AUTOMATIC_BRANCH_OFFICE);
  const [openCRMError, setOpenCRMError] = useState<boolean>(false);
  const [CRMUploadError, setCRMUploadError] = useState<null | string>(null);
  const [openCRMSuccess, setOpenCRMSuccess] = useState<boolean>(false);
  const [openExtractInfoModal, setOpenExtractInfoModal] = useState<boolean>(false);
  const [openExtractInfoError, setOpenExtractInfoError] = useState<boolean>(false);
  const [extractInfoError, setExtractInfoError] = useState<null | string>(null);
  const [openExtractInfoSuccess, setOpenExtractInfoSuccess] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [openNotesModal, setOpenNotesModal] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>(client.notes || '');
  const [anchorElForStatusMenu, setAnchorElForStatusMenu] = React.useState<null | HTMLElement>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [openCreateTicketModal, setOpenCreateTicketModal] = useState<boolean>(false);
  const [openReservationDialog, setOpenReservationDialog] = useState<boolean>(false);
  const [closeLeadMessage, setCloseLeadMessage] = useState<string>('');
  const [availableCloseStates, setAvailableCloseStates] = useState<string[]>([...ALLOWED_CLOSE_LEAD_STATUSES]);
  const [openHiddenClosureConfirmation, setOpenHiddenClosureConfirmation] = useState<boolean>(false);
  const isDemoChatClient = client.chatType === 'pusher';
  const isEmailClient = client.chatType === 'email';

  const openDropdownMenu = Boolean(anchorEl);
  const openForStatusMenu = Boolean(anchorElForStatusMenu);

  const [updateEditableAttributes] = useUpdateEditableAttributesMutation();
  const [getCreditStatus, { data: creditStatus }] = useGetCreditStatusMutation();
  const [uploadClientToCRM, { isLoading: isUploadClientToCRMLoading }] = useUploadClientToCRMMutation();
  const [extractClientInfo, { isLoading: isExtractClientInfoLoading }] = useExtractClientInfoMutation();
  const [closeLead, { isLoading: isCloseLeadLoading }] = useCloseLeadMutation();
  const { data: businessUnit } = useGetBusinessUnitQuery(client.businessUnitId, {
    skip: !client.businessUnitId,
  });
  const [markAsRead] = useMarkAsReadMutation();
  const [markAsUnread] = useMarkAsUnreadMutation();

  // Autoland Colombia: the operator picks the branch that will attend the lead, which is not always the
  // one holding the car. Every branch is offered — the upload rejects one that doesn't sell the brand.
  const canPickCrmBranchOffice = client.businessUnitId === AUTOLAND_COLOMBIA_VENTAS_BUSINESS_UNIT_ID;
  const { data: crmBranchOffices = [] } = useGetBusinessUnitBranchOfficesQuery(
    { businessUnitId: client.businessUnitId },
    { skip: !canPickCrmBranchOffice || !openUploadToCRMModal }
  );

  const onClickStatusOption = (status: string) => {
    setSelectedStatus(status);
    setOpenReservationDialog(true);
    setAnchorElForStatusMenu(null);
  };

  const handleConfirmCloseLead = (status?: string) => {
    const statusToUse = status || selectedStatus;
    if (statusToUse && !isCloseLeadLoading) {
      closeLead({
        id: client.id,
        closedState: statusToUse,
        closeLeadMessage: closeLeadMessage.trim(),
      })
        .then(() => {
          navigate('/clients');
        })
        .catch(() => {
          console.error('Error updating client');
        });
    }
  };

  const handleClickForStatusMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (businessUnit?.hasLeadClosureHidden) {
      setOpenHiddenClosureConfirmation(true);
      return;
    }
    setAnchorElForStatusMenu(event.currentTarget);
  };

  const handleCloseForStatusMenu = () => {
    setAnchorElForStatusMenu(null);
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(client.publicChatLink || window.location.href)
      .then(() => {
        setLinkCopied(true);
      })
      .catch(() => {
        console.error('Error copying link');
      });
  };

  const handleEnableAutomaticResponses = () => {
    updateEditableAttributes({
      id: client.id,
      body: {
        disabledAutomaticResponses: !client.disabledAutomaticResponses,
        ...(client.businessUnitId === 39 && // This is only for the Insurex business unit
          client.disabledAutomaticResponses === true && {
            userId: null,
          }),
      },
    })
      .then(() => {
        refetch();
      })
      .catch(() => {
        console.error('Error updating client');
      });
  };

  const handleGetCreditStatus = () => {
    getCreditStatus(client.id)
      .then(() => {
        refetch();
      })
      .catch(() => {
        console.error('Error getting credit status');
      });
  };

  const handleUploadToCRM = () => {
    if (!isUploadClientToCRMLoading) {
      const shouldUploadToGenesys = client.businessUnitId === SALFA_NURTURING_ID && uploadToGenesys;
      if (shouldUploadToGenesys && !genesysCentralizerId.trim()) {
        setGenesysCentralizerIdError(true);
        return;
      }

      uploadClientToCRM({
        clientId: client.id,
        businessUnitId: client.businessUnitId,
        uploadToGenesys: shouldUploadToGenesys,
        genesysCentralizerId: shouldUploadToGenesys ? genesysCentralizerId.trim() : undefined,
        branchOfficeName:
          canPickCrmBranchOffice && crmBranchOffice !== AUTOMATIC_BRANCH_OFFICE ? crmBranchOffice : undefined,
      })
        .then((data) => {
          console.log(data);
          setOpenUploadToCRMModal(false);
          setUploadToGenesys(false);
          setGenesysCentralizerId('');
          setGenesysCentralizerIdError(false);
          setCrmBranchOffice(AUTOMATIC_BRANCH_OFFICE);
          if ('error' in data && data.error) {
            setOpenCRMError(true);
            if ('data' in data.error && typeof data.error.data === 'string') {
              setCRMUploadError(data.error.data);
            }
          } else {
            setOpenCRMSuccess(true);
            refetch();
          }
        })
        .catch(() => {
          setOpenUploadToCRMModal(false);
          setUploadToGenesys(false);
          setGenesysCentralizerId('');
          setGenesysCentralizerIdError(false);
          setCrmBranchOffice(AUTOMATIC_BRANCH_OFFICE);
          setOpenCRMError(true);
          setCRMUploadError('Error subiendo cliente al CRM');
        });
    }
  };

  const handleExtractClientInfo = () => {
    if (!isExtractClientInfoLoading) {
      extractClientInfo(client.id)
        .then((data) => {
          console.log(data);
          setOpenExtractInfoModal(false);
          if ('error' in data && data.error) {
            setOpenExtractInfoError(true);
            if ('data' in data.error && typeof data.error.data === 'string') {
              setExtractInfoError(data.error.data);
            }
          } else {
            setOpenExtractInfoSuccess(true);
            refetch();
          }
        })
        .catch(() => {
          setOpenExtractInfoModal(false);
          setOpenExtractInfoError(true);
          setExtractInfoError('Error extrayendo información del cliente');
        });
    }
  };

  const handleSaveNotes = () => {
    updateEditableAttributes({
      id: client.id,
      body: { notes },
    })
      .then(() => {
        refetch();
        setOpenNotesModal(false);
      })
      .catch(() => {
        console.error('Error updating client notes');
      });
  };

  const handleMarkAsRead = () => {
    markAsRead(client.id)
      .catch(() => {
        console.error('Error marking client as read');
      })
      .finally(() => {
        refetch();
      });
  };

  const handleMarkAsUnread = () => {
    markAsUnread(client.id)
      .catch(() => {
        console.error('Error marking client as unread');
      })
      .finally(() => {
        refetch();
      });
  };

  const handleCloseReservationDialog = () => {
    setOpenReservationDialog(false);
    setCloseLeadMessage('');
    setSelectedStatus(null);
  };

  const handleConfirmHiddenClosure = () => {
    setOpenHiddenClosureConfirmation(false);
    handleConfirmCloseLead('Éxito');
  };

  const handleCancelHiddenClosure = () => {
    setOpenHiddenClosureConfirmation(false);
  };

  useEffect(() => {
    if (businessUnit?.customLeadClosedStates && businessUnit.customLeadClosedStates.length > 0) {
      setAvailableCloseStates(businessUnit.customLeadClosedStates);
    } else {
      setAvailableCloseStates([...ALLOWED_CLOSE_LEAD_STATUSES]);
    }
  }, [businessUnit]);

  useEffect(() => {
    if (creditStatus) {
      alert(JSON.stringify(creditStatus));
    }
  }, [creditStatus]);

  useEffect(() => {
    if (linkCopied) {
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    }
  }, [linkCopied]);

  return (
    <Grid
      size={12}
      sx={{
        px: 2,
      }}
    >
      <LoadingOverlay visible={isUploadClientToCRMLoading || isExtractClientInfoLoading} />
      {openCreateTicketModal && (
        <CreateTicketDialog
          open
          onClose={() => setOpenCreateTicketModal(false)}
          clientId={client.id}
          businessUnitId={client.businessUnitId}
          isAdmin={currentUser?.isAdmin}
        />
      )}
      {openScheduleTemplateModal && (
        <TemplateMessageModal
          client={client}
          businessUnitId={client.businessUnitId}
          open
          setOpen={setOpenScheduleTemplateModal}
          refetch={refetch}
          allowTemplateCreation={currentUser?.allowTemplateCreation || false}
        />
      )}
      <Modal open={openNotesModal} onClose={() => setOpenNotesModal(false)}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', lg: '700px' },
            backdropFilter: GLASS_BLUR,
            backgroundColor: GLASS_BACKGROUND,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            border: `1px solid ${alpha(NAVY, 0.06)}`,
            p: 3,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
              }}
            >
              {predefinedNotesBusinessUnitIds.includes(client.businessUnitId) ||
              FINANCIAL_INSTITUTION_BUSINESS_UNIT_IDS.includes(client.businessUnitId)
                ? 'Seleccionar tag'
                : 'Editar notas'}
            </Typography>
            {(() => {
              if (predefinedNotesBusinessUnitIds.includes(client.businessUnitId)) {
                const predefinedOptions = [
                  'VENTA OK',
                  'PENDIENTE FIRMA',
                  'FALTA PÓLIZA',
                  'PENDIENTE DPS',
                  'PENDIENTE FECHA OTORGAMIENTO',
                  'NO CALIFICA',
                  'NO ACEPTA',
                ];
                const isCustomText = !predefinedOptions.includes(notes);

                return (
                  <Stack spacing={2}>
                    {predefinedOptions.map((option) => (
                      <Button
                        key={option}
                        variant={notes === option ? 'contained' : 'outlined'}
                        color={notes === option ? 'primary' : 'inherit'}
                        onClick={() => setNotes(option)}
                        fullWidth
                        sx={{ py: 1.5 }}
                      >
                        {option}
                      </Button>
                    ))}

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={isCustomText ? notes : ''}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Nota personalizada..."
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Stack>
                );
              }

              if (FINANCIAL_INSTITUTION_BUSINESS_UNIT_IDS.includes(client.businessUnitId)) {
                const predefinedOptions = [
                  'RECHAZADO',
                  'PENDIENTE',
                  'EN COMITÉ',
                  'CURSADO',
                  'DESACTIVAR MARKETING',
                  'OTROS PRODUCTOS',
                ];
                const isCustomText = !predefinedOptions.includes(notes);

                return (
                  <Stack spacing={2}>
                    {predefinedOptions.map((option) => (
                      <Button
                        key={option}
                        variant={notes === option ? 'contained' : 'outlined'}
                        color={notes === option ? 'primary' : 'inherit'}
                        onClick={() => setNotes(option)}
                        fullWidth
                        sx={{ py: 1.5 }}
                      >
                        {option}
                      </Button>
                    ))}

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={isCustomText ? notes : ''}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="O escribe texto personalizado..."
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Stack>
                );
              }

              return (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ingresa tus notas aquí..."
                  variant="outlined"
                />
              );
            })()}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 3,
              }}
            >
              <PrimaryBtn onClick={() => setOpenNotesModal(false)} variant="outlined" color="inherit">
                Cancelar
              </PrimaryBtn>
              <PrimaryBtn onClick={handleSaveNotes} variant="outlined" disabled={notes === client.notes}>
                Guardar
              </PrimaryBtn>
            </Box>
          </Box>
        </Paper>
      </Modal>
      <OptionsDialog
        openDialog={openUploadToCRMModal && !isUploadClientToCRMLoading}
        setOpenDialog={(open) => {
          setOpenUploadToCRMModal(open);
          if (!open) {
            setUploadToGenesys(false);
            setGenesysCentralizerId('');
            setGenesysCentralizerIdError(false);
            setCrmBranchOffice(AUTOMATIC_BRANCH_OFFICE);
          }
        }}
        header="Subir lead al CRM"
        description="¿Estás seguro que deseas subir este lead al CRM?"
        extraContent={
          canPickCrmBranchOffice ? (
            <TextField
              fullWidth
              helperText="Automática (por defecto) usa la sucursal del auto o de la conversación. Elige una sólo si el cliente pide otra sede."
              label="Sucursal que atenderá el lead"
              onChange={(event) => setCrmBranchOffice(event.target.value)}
              select
              sx={{ mt: 1.5 }}
              value={crmBranchOffice}
            >
              <MenuItem value={AUTOMATIC_BRANCH_OFFICE}>Automática</MenuItem>
              {crmBranchOffices.map((branchOfficeName) => (
                <MenuItem key={branchOfficeName} value={branchOfficeName}>
                  {branchOfficeName}
                </MenuItem>
              ))}
            </TextField>
          ) : client.businessUnitId === SALFA_NURTURING_ID ? (
            <Box sx={{ mt: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uploadToGenesys}
                    onChange={(event) => {
                      setUploadToGenesys(event.target.checked);
                      if (!event.target.checked) {
                        setGenesysCentralizerId('');
                        setGenesysCentralizerIdError(false);
                      }
                    }}
                    sx={{ color: alpha(NAVY, 0.55), '&.Mui-checked': { color: NAVY } }}
                  />
                }
                label="Crear también el lead en Genesys"
                sx={{ color: NAVY, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
              {uploadToGenesys && (
                <TextField
                  autoFocus
                  error={genesysCentralizerIdError}
                  fullWidth
                  helperText={
                    genesysCentralizerIdError
                      ? 'Ingresa el ID centralizador para crear el lead en Genesys.'
                      : 'Este ID es obligatorio para crear el lead en Genesys.'
                  }
                  label="ID centralizador"
                  onChange={(event) => {
                    setGenesysCentralizerId(event.target.value);
                    if (event.target.value.trim()) setGenesysCentralizerIdError(false);
                  }}
                  sx={{ mt: 1 }}
                  value={genesysCentralizerId}
                />
              )}
            </Box>
          ) : undefined
        }
      >
        <PrimaryBtn
          color="error"
          onClick={() => {
            setOpenUploadToCRMModal(false);
            setUploadToGenesys(false);
            setGenesysCentralizerId('');
            setGenesysCentralizerIdError(false);
          }}
        >
          Cancelar
        </PrimaryBtn>
        <PrimaryBtn onClick={handleUploadToCRM}>Subir</PrimaryBtn>
      </OptionsDialog>
      <OptionsDialog
        openDialog={openExtractInfoModal && !isExtractClientInfoLoading}
        setOpenDialog={setOpenExtractInfoModal}
        header="Extraer información del cliente"
        description="¿Deseas ejecutar los agentes de IA para extraer y actualizar la información del cliente?"
      >
        <PrimaryBtn color="error" onClick={() => setOpenExtractInfoModal(false)}>
          Cancelar
        </PrimaryBtn>
        <PrimaryBtn onClick={handleExtractClientInfo}>Extraer</PrimaryBtn>
      </OptionsDialog>
      <AlertDialog
        openDialog={openCRMError}
        setOpenDialog={setOpenCRMError}
        header="Error"
        msg={CRMUploadError || 'Error subiendo cliente al CRM'}
        cancelMsg="Cerrar"
      />
      <AlertDialog
        openDialog={openExtractInfoError}
        setOpenDialog={setOpenExtractInfoError}
        header="Error"
        msg={extractInfoError || 'Error extrayendo información del cliente'}
        cancelMsg="Cerrar"
      />
      <OptionsDialog
        openDialog={openHiddenClosureConfirmation}
        setOpenDialog={setOpenHiddenClosureConfirmation}
        header="Cerrar lead"
        description="¿Estás seguro que deseas cerrar este lead?"
      >
        <PrimaryBtn color="error" onClick={handleCancelHiddenClosure}>
          Cancelar
        </PrimaryBtn>
        <PrimaryBtn onClick={handleConfirmHiddenClosure}>Cerrar lead</PrimaryBtn>
      </OptionsDialog>
      {openCRMSuccess && <InfoDialog header="Cliente subido al CRM" msg="Cliente subido al CRM con éxito!" />}
      {openExtractInfoSuccess && (
        <InfoDialog
          header="Información extraída"
          msg="La información del cliente ha sido extraída y actualizada exitosamente!"
        />
      )}
      {/* Close Lead Dialog */}
      <Dialog
        open={openReservationDialog}
        onClose={handleCloseReservationDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: RADIUS_CARD,
              backgroundColor: GLASS_BACKGROUND,
              backdropFilter: GLASS_BLUR,
              boxShadow: SHADOW,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            fontSize: '1.1rem',
            color: NAVY,
            pt: 3,
            pb: 1,
            px: 3,
          }}
        >
          Comentario adicional
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.9rem',
              color: alpha(NAVY, 0.65),
              mb: 2,
            }}
          >
            Antes de cerrar el lead, puedes agregar un comentario adicional.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Comentario"
            type="text"
            fullWidth
            variant="outlined"
            value={closeLeadMessage}
            onChange={(e) => setCloseLeadMessage(e.target.value)}
            placeholder="Deja aquí razones de éxito/fracaso, o cualquier comentario adicional."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: RADIUS_ITEM,
                backgroundColor: SURFACE_TINT,
                fontFamily: 'Jost, sans-serif',
                color: NAVY,
                '& fieldset': { borderColor: alpha(NAVY, 0.08) },
                '&:hover fieldset': { borderColor: alpha(NAVY, 0.15) },
                '&.Mui-focused fieldset': { borderColor: alpha(YELLOW, 0.6) },
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: alpha(NAVY, 0.8),
                '&.Mui-focused': { color: NAVY },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseReservationDialog}
            sx={{
              textTransform: 'none',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 500,
              color: alpha(NAVY, 0.6),
              borderRadius: RADIUS_COIN,
              px: 2,
              '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleConfirmCloseLead()}
            disabled={isCloseLeadLoading}
            startIcon={isCloseLeadLoading ? <CircularProgress size={16} sx={{ color: alpha(NAVY, 0.5) }} /> : null}
            sx={{
              textTransform: 'none',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              borderRadius: RADIUS_COIN,
              backgroundColor: YELLOW,
              color: NAVY,
              px: 3,
              boxShadow: 'none',
              '&:hover': { backgroundColor: alpha(YELLOW, 0.85), boxShadow: 'none' },
              '&.Mui-disabled': { backgroundColor: alpha(YELLOW, 0.35), color: alpha(NAVY, 0.4) },
            }}
          >
            {isCloseLeadLoading ? 'Cerrando lead…' : 'Cerrar lead'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {!isDemoChatClient && !isEmailClient && <WhatsAppWindowBadge windowExpiresAt={windowExpiresAt} />}
          {!isEmailClient && (
            <Tooltip title="Enviar plantilla" arrow>
              <span>
                <IconButton
                  onClick={() => setOpenScheduleTemplateModal(true)}
                  size="medium"
                  sx={{
                    color: alpha(NAVY, 0.6),
                    '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
                    '&.Mui-disabled': { color: alpha(NAVY, 0.25) },
                  }}
                >
                  <SendOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={client.unreadMessagesCount > 0 ? 'Marcar como leído' : 'Marcar como no leído'} arrow>
            <IconButton
              onClick={client.unreadMessagesCount > 0 ? handleMarkAsRead : handleMarkAsUnread}
              size="medium"
              sx={{
                color: alpha(NAVY, 0.6),
                '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
              }}
            >
              {client.unreadMessagesCount > 0 ? (
                <MarkChatReadOutlinedIcon fontSize="small" />
              ) : (
                <MarkChatUnreadOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Recargar conversación" arrow>
            {isLoading ? (
              <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={18} sx={{ color: alpha(NAVY, 0.6) }} />
              </Box>
            ) : (
              <IconButton
                onClick={refetch}
                size="medium"
                sx={{
                  color: alpha(NAVY, 0.6),
                  '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
          </Tooltip>
          <IconButton
            onClick={handleDropdownClick}
            size="medium"
            sx={{
              color: alpha(NAVY, 0.6),
              '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            open={openDropdownMenu}
            onClose={handleDropdownClose}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                sx: {
                  backdropFilter: GLASS_BLUR,
                  backgroundColor: GLASS_BACKGROUND,
                  boxShadow: SHADOW,
                  borderRadius: RADIUS_CARD,
                  border: `1px solid ${alpha(NAVY, 0.06)}`,
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setOpenNotesModal(true);
                handleDropdownClose();
              }}
            >
              Editar notas
            </MenuItem>
            {currentUser?.isAdmin && (
              <MenuItem
                onClick={() => {
                  setOpenExtractInfoModal(true);
                  handleDropdownClose();
                }}
              >
                Extraer información
              </MenuItem>
            )}
            <MenuItem
              onClick={handleEnableAutomaticResponses}
              disabled={!canChangeAutomaticResponses(client.lastLeadStatus)}
            >
              {client.disabledAutomaticResponses ? 'Habilitar' : 'Deshabilitar'} IA
            </MenuItem>
            {client.creditSimulations && client.creditSimulations.length > 0 && (
              <MenuItem onClick={handleGetCreditStatus}>Estado de simulación de crédito</MenuItem>
            )}
            <MenuItem onClick={handleCopyLink} disabled={linkCopied}>
              {linkCopied ? 'Link copiado' : 'Compartir link'}
            </MenuItem>
            {uploadableBusinessIds.includes(client.businessUnitId) && (
              <MenuItem onClick={() => setOpenUploadToCRMModal(true)}>Subir al CRM</MenuItem>
            )}
            <MenuItem onClick={() => setOpenCreateTicketModal(true)}>Crear ticket de soporte</MenuItem>
            {currentUser?.isAdmin && (
              <MenuItem>
                <a
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  href={`https://console.cloud.google.com/logs/query;query=%22%5Bc${client.id}%5D%22;cursorTimestamp=2025-09-29T13:19:21.789882Z;duration=P30D?project=saas-relif`}
                  target="_blank"
                  rel="noreferrer"
                >
                  [ADM] Ver logs del cliente
                </a>
              </MenuItem>
            )}
          </Menu>

          {client.businessUnit?.hasSellerManagement && (
            <>
              <Tooltip
                title={
                  isLastLeadClosed ? 'Lead ya está cerrado' : 'Cerrar lead. No estarás asignado y se habilitará la IA'
                }
                arrow
              >
                <span>
                  <IconButton
                    onClick={handleClickForStatusMenu}
                    disabled={isLastLeadClosed}
                    size="medium"
                    sx={{
                      color: '#E7233B',
                      '&:hover': { backgroundColor: alpha('#E7233B', 0.08) },
                      '&.Mui-disabled': { color: alpha('#E7233B', 0.35) },
                    }}
                  >
                    <HighlightOffIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={anchorElForStatusMenu}
                open={openForStatusMenu}
                onClose={handleCloseForStatusMenu}
                slotProps={{
                  paper: {
                    sx: {
                      backdropFilter: GLASS_BLUR,
                      backgroundColor: GLASS_BACKGROUND,
                      boxShadow: SHADOW,
                      borderRadius: RADIUS_CARD,
                      border: `1px solid ${alpha(NAVY, 0.06)}`,
                    },
                  },
                }}
              >
                {availableCloseStates.map((status: string) => (
                  <MenuItem key={status} onClick={() => onClickStatusOption(status)}>
                    {status}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {currentUser?.isAdmin && (
            <Tooltip title="Filtrar conversación" arrow>
              <IconButton
                onClick={() => setIsFilteredConversation(!isFilteredConversation)}
                size="medium"
                sx={{
                  color: isFilteredConversation ? NAVY : alpha(NAVY, 0.6),
                  '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
                }}
              >
                <FilterAlt fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default ChatHeader;
